import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Bell, Check, CheckCheck, X } from 'lucide-react';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 15000); // Poll every 15s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadNotifications = async () => {
    try {
      const r = await api.get('/api/notifications');
      setNotifications(r.data?.notifications || []);
      setUnread(r.data?.unread_count || 0);
    } catch (e) { console.error(e); }
  };

  const markRead = async (id) => {
    try { await api.patch(`/api/notifications/${id}/read`); loadNotifications(); } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try { await api.post('/api/notifications/read-all'); loadNotifications(); } catch (e) { console.error(e); }
  };

  const formatTime = (ts) => {
    const d = new Date(ts); const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: 6 }} data-testid="notification-bell">
        <Bell size={20} color="#64748b" />
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, borderRadius: '50%', background: '#dc2626', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 40, right: 0, width: 360, background: 'white', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', zIndex: 100, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: 'var(--panzix-green)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} data-testid="mark-all-read">
                <CheckCheck size={14} />Mark all read
              </button>
            )}
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No notifications</div>
            ) : (
              notifications.slice(0, 20).map(n => (
                <div key={n.id} onClick={() => !n.read && markRead(n.id)}
                  style={{ padding: '12px 16px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', background: n.read ? 'white' : '#f0fdf4', transition: 'background 0.15s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: n.read ? 400 : 700, color: '#0f172a' }}>{n.title}</span>
                    {!n.read && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--panzix-green)', flexShrink: 0, marginTop: 4 }} />}
                  </div>
                  <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>{n.message}</p>
                  <span style={{ fontSize: 10, color: '#cbd5e1' }}>{formatTime(n.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
